import { call, put } from 'redux-saga/effects';
import { SubmissionError } from 'redux-form';

import apiClient from '../clients/apiClient';

/**
 * API Saga Generator
 * Use this to yield API responses from the apiClient.
 * The second argument may be an array of options objects, resulting in a call to apiClient.all.
 *
 * @param {Object}   routine         Set of actions packed up by redux-saga-routines.
 * @param {String}   options.method  HTTP method.
 * @param {String}   options.url     URL to hit.
 * @param {Object}   options.data    Data to send along as params/body data.
 * @param {Object}   options.headers Data to send along as headers data.
 * @param {Boolean}  options.silent  If silent, don't run the “request” action.
 * @param {Function} transform       Should transform the response before commiting the "success" action.
 * @param {boolean} isSubmission     If the apiSaga is form submission. If yes - it will handle errors as SubmissionErrors,
 *                                   and pass the error text from backend to fields.
 * @yield {Object}                   The API call response.
 */
export default function* apiSaga(
  routine,
  {
    method = 'get',
    url = '',
    data = {},
    headers = {},
    silent = false,
    callback = () => {},
    callbackValue = true,
    onReady = () => {}, //@todo - to be refactor with callback and callbackValue
  },
  transform = (x) => x,
  isSubmission = false,
  isParallel = true,
) {
  try {
    let response = null;

    if (!silent) {
      yield put(routine.request());
    }

    const arg2 = arguments[1];
    const shouldUseAll = Array.isArray(arg2);
    if (shouldUseAll) {
      if (isParallel) {
        response = yield call(apiClient.all, arg2);
      } else {
        response = [];
        for (let i = 0; i < arg2.length; i++) {
          const { method: cMethod, url: cUrl, data: cData, headers } = arg2[i];
          const reqResponse = yield call(
            apiClient[cMethod],
            cUrl,
            cData,
            headers,
          );
          response.push(reqResponse);
        }
      }
    } else {
      response = yield call(apiClient[method], url, data, headers);
    }
    if (isSubmission && response.errors) {
      throw new SubmissionError(response.errors);
    } else if (isSubmission && response?.length) {
      response?.forEach((res) => {
        if (res?.errors) {
          throw new SubmissionError(res.errors);
        }
      });
    }
    yield put(routine.success(transform(response)));
    onReady(transform(response));

    return response;
  } catch (error) {
    yield put(routine.failure(error));
    return error;
  } finally {
    typeof callback === 'function' && callback(callbackValue);
  }
}
